/**
 * src/api/vendorRegistryApi.js
 *
 * Nordic business registry lookups.
 *
 * Supported countries:
 *   DK — CVR (cvrapi.dk)
 *   NO — Brønnøysund Register Centre (data.brreg.no)
 *   FI — Finnish Patent and Registration Office (avoindata.prh.fi)
 *   SE — Manual entry only (no free browser-accessible API)
 *
 * All public APIs used here are CORS-enabled.
 *
 * Registry data model stored on vendor:
 *   registryCountry     'DK' | 'NO' | 'FI' | 'SE' | null
 *   registryId          CVR / org-number / business-id
 *   registryData        { name, address, city, zipcode, country, status, industry,
 *                         founded, employees, bankrupt, inLiquidation, dissolved,
 *                         email, phone, website, companyType, raw }
 *   registryRiskScore   1–5  (computed from registryData)
 *   lastRegistrySync    ISO timestamp of last successful sync
 *
 * The external registries cannot be called from the browser (cvrapi.dk requires
 * a custom User-Agent and is not CORS-enabled; PRH's v1 endpoint is retired), so
 * the actual lookups run server-side in the `vendor-registry-lookup` Azure
 * Function. The functions below delegate to it and keep the normalised shape the
 * rest of the app already consumes. The pure helpers (risk scoring, domain guess,
 * industry mapping, country meta) stay client-side.
 */

import { azureGet, azurePost } from './azureClient.js';

// ── Country meta ──────────────────────────────────────────────────────────────

/**
 * Countries with a live registry lookup wired up server-side
 * (azure-functions/vendor-registry-lookup/index.js's `action=fetch` guard).
 * Every other REGISTRY_COUNTRIES entry is manual-entry-only — offering a
 * "Refresh from registry" action for one of those is a doomed API call, not
 * a real feature (GH #5418).
 */
export const LIVE_LOOKUP_COUNTRIES = ['DK', 'NO', 'FI'];

export const REGISTRY_COUNTRIES = [
  { code: 'DK', label: 'Denmark (CVR)', idLabel: 'CVR Number', placeholder: '12345678', digits: 8 },
  {
    code: 'NO',
    label: 'Norway (BRREG)',
    idLabel: 'Org. Number',
    placeholder: '123456789',
    digits: 9,
  },
  {
    code: 'FI',
    label: 'Finland (PRH)',
    idLabel: 'Business ID',
    placeholder: '1234567-8',
    digits: null,
  },
  // Everything below has no live registry lookup wired up — country + ID are
  // entered manually, same as Sweden always has been.
  {
    code: 'SE',
    label: 'Sweden (manual)',
    idLabel: 'Org. Number',
    placeholder: '556000-0000',
    digits: null,
  },
  {
    code: 'AL',
    label: 'Albania (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'AD',
    label: 'Andorra (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'AT',
    label: 'Austria (manual)',
    idLabel: 'Firmenbuchnummer',
    placeholder: '',
    digits: null,
  },
  {
    code: 'BY',
    label: 'Belarus (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'BE',
    label: 'Belgium (manual)',
    idLabel: 'Enterprise Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'BA',
    label: 'Bosnia and Herzegovina (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  { code: 'BG', label: 'Bulgaria (manual)', idLabel: 'UIC', placeholder: '', digits: null },
  { code: 'HR', label: 'Croatia (manual)', idLabel: 'OIB', placeholder: '', digits: null },
  {
    code: 'CY',
    label: 'Cyprus (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  { code: 'CZ', label: 'Czechia (manual)', idLabel: 'IČO', placeholder: '', digits: null },
  {
    code: 'EE',
    label: 'Estonia (manual)',
    idLabel: 'Registry Code',
    placeholder: '',
    digits: null,
  },
  { code: 'FR', label: 'France (manual)', idLabel: 'SIREN', placeholder: '', digits: null },
  { code: 'DE', label: 'Germany (manual)', idLabel: 'HRB Number', placeholder: '', digits: null },
  { code: 'GR', label: 'Greece (manual)', idLabel: 'GEMI Number', placeholder: '', digits: null },
  {
    code: 'HU',
    label: 'Hungary (manual)',
    idLabel: 'Company Reg. Number',
    placeholder: '',
    digits: null,
  },
  { code: 'IS', label: 'Iceland (manual)', idLabel: 'Kennitala', placeholder: '', digits: null },
  { code: 'IE', label: 'Ireland (manual)', idLabel: 'CRO Number', placeholder: '', digits: null },
  { code: 'IT', label: 'Italy (manual)', idLabel: 'Partita IVA', placeholder: '', digits: null },
  {
    code: 'XK',
    label: 'Kosovo (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'LV',
    label: 'Latvia (manual)',
    idLabel: 'Registration Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'LI',
    label: 'Liechtenstein (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'LT',
    label: 'Lithuania (manual)',
    idLabel: 'Company Code',
    placeholder: '',
    digits: null,
  },
  {
    code: 'LU',
    label: 'Luxembourg (manual)',
    idLabel: 'RCS Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'MT',
    label: 'Malta (manual)',
    idLabel: 'Registration Number',
    placeholder: '',
    digits: null,
  },
  { code: 'MD', label: 'Moldova (manual)', idLabel: 'IDNO', placeholder: '', digits: null },
  {
    code: 'MC',
    label: 'Monaco (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'ME',
    label: 'Montenegro (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'NL',
    label: 'Netherlands (manual)',
    idLabel: 'KVK Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'MK',
    label: 'North Macedonia (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'PL',
    label: 'Poland (manual)',
    idLabel: 'KRS/NIP Number',
    placeholder: '',
    digits: null,
  },
  { code: 'PT', label: 'Portugal (manual)', idLabel: 'NIPC', placeholder: '', digits: null },
  { code: 'RO', label: 'Romania (manual)', idLabel: 'CUI', placeholder: '', digits: null },
  {
    code: 'SM',
    label: 'San Marino (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'RS',
    label: 'Serbia (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  { code: 'SK', label: 'Slovakia (manual)', idLabel: 'IČO', placeholder: '', digits: null },
  {
    code: 'SI',
    label: 'Slovenia (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  { code: 'ES', label: 'Spain (manual)', idLabel: 'CIF/NIF', placeholder: '', digits: null },
  { code: 'CH', label: 'Switzerland (manual)', idLabel: 'UID', placeholder: '', digits: null },
  { code: 'UA', label: 'Ukraine (manual)', idLabel: 'EDRPOU', placeholder: '', digits: null },
  { code: 'GB', label: 'United Kingdom (manual)', idLabel: 'CRN', placeholder: '', digits: null },
  {
    code: 'VA',
    label: 'Vatican City (manual)',
    idLabel: 'Registry Number',
    placeholder: '',
    digits: null,
  },
  { code: 'US', label: 'United States (manual)', idLabel: 'EIN', placeholder: '', digits: null },
  {
    code: 'CA',
    label: 'Canada (manual)',
    idLabel: 'Business Number',
    placeholder: '',
    digits: null,
  },
  { code: 'AU', label: 'Australia (manual)', idLabel: 'ABN', placeholder: '', digits: null },
  { code: 'NZ', label: 'New Zealand (manual)', idLabel: 'NZBN', placeholder: '', digits: null },
  {
    code: 'JP',
    label: 'Japan (manual)',
    idLabel: 'Corporate Number',
    placeholder: '',
    digits: null,
  },
  { code: 'CN', label: 'China (manual)', idLabel: 'USCC', placeholder: '', digits: null },
  { code: 'IN', label: 'India (manual)', idLabel: 'CIN', placeholder: '', digits: null },
  { code: 'SG', label: 'Singapore (manual)', idLabel: 'UEN', placeholder: '', digits: null },
  {
    code: 'AE',
    label: 'United Arab Emirates (manual)',
    idLabel: 'Trade License Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'IL',
    label: 'Israel (manual)',
    idLabel: 'Company Number',
    placeholder: '',
    digits: null,
  },
  { code: 'BR', label: 'Brazil (manual)', idLabel: 'CNPJ', placeholder: '', digits: null },
  { code: 'MX', label: 'Mexico (manual)', idLabel: 'RFC', placeholder: '', digits: null },
  {
    code: 'ZA',
    label: 'South Africa (manual)',
    idLabel: 'Registration Number',
    placeholder: '',
    digits: null,
  },
  {
    code: 'XX',
    label: 'Other / Not listed (manual)',
    idLabel: 'Registry ID',
    placeholder: '',
    digits: null,
  },
];

// ── Risk score computation ────────────────────────────────────────────────────

/**
 * Compute a 1–5 registry risk score from normalised registry data.
 * 1 = All clear, 5 = Critical (bankrupt / dissolved).
 */
export function computeRegistryRiskScore(registryData) {
  if (!registryData) return 1;

  if (registryData.bankrupt || registryData.dissolved) return 5;
  if (registryData.inLiquidation || registryData.forcedWinding) return 4;

  // Young company (< 2 years) — slightly elevated
  if (registryData.founded) {
    const ageYears =
      (Date.now() - new Date(registryData.founded).getTime()) / (365.25 * 24 * 3600 * 1000);
    if (ageYears < 1) return 3;
    if (ageYears < 2) return 2;
  }

  return 1;
}

/**
 * Compute adjusted residual score that includes the registry risk factor.
 * Registry penalty adds up to +0.8 to the base residual (capped at 5).
 */
export function computeRegistryAdjustedResidual(baseResidual, registryRiskScore) {
  if (!registryRiskScore || registryRiskScore <= 1) return baseResidual;
  const penalty = (registryRiskScore - 1) * 0.2; // 0.2 per point above 1, max 0.8
  return Math.min(5, baseResidual + penalty);
}

// ── Domain / contact helpers ──────────────────────────────────────────────────

/**
 * Best-effort guess at a company's primary domain from its registered name.
 * Returns a bare domain string (no protocol) or '' if unable to guess.
 */
export function guessCompanyDomain(name, country) {
  if (!name) return '';

  // Strip common legal suffixes
  let clean = name
    .replace(
      /\s+(a\/s|aps|apS|a\.s\.|as|oy abp|oyj|oy|ab|gmbh|ltd\.?|llc|inc\.?|co\.?|plc|sa|nv|bv|srl|spa|ag|kg|kft|sas|sarl|asa|se)\s*$/i,
      ''
    )
    .trim();

  // Remove everything that is not a letter or digit
  clean = clean.toLowerCase().replace(/[^a-z0-9]/g, '');

  if (!clean) return '';

  const tldMap = { DK: '.dk', NO: '.no', FI: '.fi', SE: '.se' };
  const tld = tldMap[country] || '.com';

  return clean + tld;
}

/**
 * Map an industry description (from any registry) to one of SeaSuite's vendor categories.
 * Returns null if no confident match is found.
 */
export function mapIndustryToCategory(industry) {
  if (!industry) return null;
  const lc = industry.toLowerCase();

  if (/cloud|hosting|iaas|paas|coloc|data.?cent|infrastructure as/.test(lc))
    return 'Cloud Provider';
  if (
    /software|saas|platform|digital|app\b|web.?dev|it.?service|system.?dev|it-service|edb/.test(lc)
  )
    return 'SaaS';
  if (/\bdata\b|processing|analytics|database|storage|datab/.test(lc)) return 'Data Processor';
  if (/hardware|equipment|device|electronic|manufactur|component/.test(lc)) return 'Hardware';
  if (/consult|advisory|management.?service|audit|accounting|legal|law|certified/.test(lc))
    return 'Professional Services';
  if (/managed.?service|outsourc|facility|operation|support.?service/.test(lc))
    return 'Managed Service';
  if (/sub.?process|process|partner/.test(lc)) return 'Subprocessor';

  return null;
}

// ── Public API ────────────────────────────────────────────────────────────────
//
// The actual registry calls run server-side in the `vendor-registry-lookup`
// Azure Function (see that function for the per-registry endpoints, the CVR
// User-Agent requirement, and the normalisers). These wrappers just call it and
// return the normalised results unchanged.

// ── Vendor assessment portal ──────────────────────────────────────────────────

/**
 * Email a vendor a one-time link to complete their security self-assessment.
 * Authenticated + plan-gated server-side; tenant resolved from the JWT.
 */
export async function sendVendorAssessmentLink(vendorId) {
  return azurePost('/api/vendor-assessment-send-link', { vendorId });
}

/**
 * Search for companies by name in a specific country's registry.
 * Returns array of normalised registry results.
 */
export async function searchVendorRegistry(query, country) {
  if (!query?.trim() || !country) return [];
  try {
    const { results } = await azureGet('/api/vendor-registry-lookup', {
      action: 'search',
      country,
      q: query.trim(),
    });
    return Array.isArray(results) ? results : [];
  } catch {
    return [];
  }
}

/**
 * Search all supported registries (DK, NO, FI) simultaneously.
 * Results are interleaved: DK first, then NO, then FI.
 * Each registry failure is swallowed server-side so partial results still show.
 * Requires at least 2 characters to avoid hammering the APIs.
 */
export async function searchAllRegistries(query) {
  if (!query?.trim() || query.trim().length < 2) return [];
  try {
    const { results } = await azureGet('/api/vendor-registry-lookup', {
      action: 'search',
      country: 'ALL',
      q: query.trim(),
    });
    return Array.isArray(results) ? results : [];
  } catch {
    return [];
  }
}

/**
 * Fetch a single company by registry ID (for monthly refresh).
 * Returns normalised registry data or null on failure.
 */
export async function fetchVendorByRegistryId(registryId, country) {
  if (!registryId || !country) return null;
  try {
    // silent: true — every caller already catches a failure here and shows
    // its own message (VendorDetailModal's refreshError,
    // vendorRegistrySync's own sweeper logging); the generic banner/auto-
    // report was pure duplicate noise on top of that (GH #5418).
    const { result } = await azureGet(
      '/api/vendor-registry-lookup',
      { action: 'fetch', country, registryId },
      { silent: true }
    );
    return result || null;
  } catch {
    return null;
  }
}

/**
 * Look up a Danish postal code and return its city name (DAWA / postnumre).
 * Returns { zip, city } or null if the code doesn't match / lookup fails.
 * Currently DK only — no equivalent free lookup wired up for NO/FI/SE yet.
 */
export async function lookupDanishPostcode(zip) {
  if (!zip?.trim()) return null;
  try {
    const { result } = await azureGet('/api/vendor-registry-lookup', {
      action: 'postcode',
      country: 'DK',
      zip: zip.trim(),
    });
    return result || null;
  } catch {
    return null;
  }
}
